<template>
    <v-dialog v-model="dialog" width="620" persistent scrollable>
        <v-card>
            <v-card-title class="text-subtitle-1">
                {{ form.id ? 'Editar regra de alerta' : 'Nova regra de alerta' }}
                <v-spacer/>
                <v-btn icon @click="fechar"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>

            <v-divider/>

            <v-card-text class="pt-4">
                <v-form ref="form">
                    <v-text-field
                        v-model="form.nome"
                        label="Nome da regra"
                        outlined
                        dense
                        :rules="[v => !!v || 'Informe um nome']"
                    />

                    <v-select
                        v-model="form.metrica"
                        :items="opcoesMetricas"
                        item-text="label"
                        item-value="coluna"
                        label="Métrica"
                        outlined
                        dense
                        :rules="[v => !!v || 'Escolha uma métrica']"
                    />

                    <v-row dense>
                        <v-col cols="4">
                            <v-select
                                v-model="form.operador"
                                :items="operadores"
                                label="Condição"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="4">
                            <v-text-field
                                v-model.number="form.limite"
                                label="Limite"
                                type="number"
                                outlined
                                dense
                                :suffix="unidade"
                            />
                        </v-col>
                        <v-col cols="4">
                            <v-select
                                v-model="form.severidade"
                                :items="[
                                    { text: 'Aviso', value: 'aviso' },
                                    { text: 'Crítico', value: 'critico' },
                                ]"
                                label="Severidade"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>

                    <v-row dense>
                        <v-col cols="6">
                            <v-text-field
                                v-model.number="form.duracao_minutos"
                                label="Sustentado por"
                                type="number"
                                outlined
                                dense
                                suffix="min"
                                hint="Só dispara se violar em todas as coletas da janela"
                                persistent-hint
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model.number="form.cooldown_minutos"
                                label="Repetir no máximo a cada"
                                type="number"
                                outlined
                                dense
                                suffix="min"
                                hint="Evita uma enxurrada de e-mails do mesmo problema"
                                persistent-hint
                            />
                        </v-col>
                    </v-row>

                    <v-select
                        v-model="form.escopo"
                        :items="escopos"
                        label="Aplica-se a"
                        outlined
                        dense
                        class="mt-2"
                    />

                    <v-select
                        v-if="form.escopo !== 'todos'"
                        v-model="form.hosts"
                        :items="opcoesServidores"
                        item-text="nome"
                        item-value="host"
                        :label="form.escopo === 'apenas' ? 'Servidores' : 'Servidores excluídos'"
                        outlined
                        dense
                        multiple
                        small-chips
                        deletable-chips
                        :rules="[v => (v && v.length > 0) || 'Escolha ao menos um servidor']"
                    />

                    <v-combobox
                        v-model="form.destinatarios"
                        label="Destinatários"
                        outlined
                        dense
                        multiple
                        small-chips
                        deletable-chips
                        append-icon=""
                        class="mt-4"
                        hint="Digite o e-mail e pressione Enter"
                        persistent-hint
                    />

                    <v-textarea
                        v-model="form.observacao"
                        label="O que fazer quando disparar"
                        outlined
                        dense
                        rows="2"
                        class="mt-4"
                        hint="Vai junto no corpo do e-mail"
                        persistent-hint
                    />

                    <div class="d-flex flex-wrap mt-2">
                        <v-switch
                            v-model="form.notificar_recuperacao"
                            label="Avisar quando normalizar"
                            dense
                            inset
                            hide-details
                            class="mr-6"
                        />
                        <v-switch
                            v-model="form.ativo"
                            label="Regra ativa"
                            dense
                            inset
                            hide-details
                        />
                    </div>
                </v-form>
            </v-card-text>

            <v-divider/>

            <v-card-actions>
                <v-btn v-if="podeEditar" text small :disabled="!form.id" @click="$emit('testar', form)">
                    <v-icon left small>mdi-email-fast-outline</v-icon>
                    Enviar e-mail de teste
                </v-btn>
                <v-spacer/>
                <v-btn text @click="fechar">{{ podeEditar ? 'Cancelar' : 'Fechar' }}</v-btn>
                <v-btn v-if="podeEditar" color="primary" @click="salvar">Salvar</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { catalogo, grupos, metrica } from './catalogo';
import { ativos } from './servidores';

const VAZIA = {
    id: null,
    nome: '',
    metrica: '',
    operador: '>',
    limite: 0,
    duracao_minutos: 5,
    severidade: 'aviso',
    destinatarios: [],
    cooldown_minutos: 60,
    notificar_recuperacao: true,
    escopo: 'todos',
    hosts: [],
    ativo: true,
    observacao: '',
};

export default {
    name: 'RegraDialog',

    data: () => ({
        dialog: false,
        form: { ...VAZIA },
        operadores: [
            { text: 'maior que', value: '>' },
            { text: 'maior ou igual', value: '>=' },
            { text: 'menor que', value: '<' },
            { text: 'menor ou igual', value: '<=' },
        ],
        escopos: [
            { text: 'Todos os servidores', value: 'todos' },
            { text: 'Apenas os escolhidos', value: 'apenas' },
            { text: 'Todos, exceto os escolhidos', value: 'exceto' },
        ],
    }),

    computed: {
        opcoesServidores() {
            return ativos();
        },

        opcoesMetricas() {
            const itens = [];

            grupos().forEach(grupo => {
                itens.push({ header: grupo });
                catalogo().filter(m => m.grupo === grupo).forEach(m => itens.push(m));
            });

            return itens;
        },

        unidade() {
            return this.form.metrica ? metrica(this.form.metrica).unidade : '';
        },

        podeEditar() {
            return this.checkPermission('zapercontrol_monitoramento_regras') || this.isDiretor();
        },
    },

    methods: {
        abrir(regra) {
            this.form = regra
                ? {
                    ...VAZIA,
                    ...regra,
                    destinatarios: [...regra.destinatarios],
                    hosts: [...(regra.hosts || [])],
                }
                : { ...VAZIA, destinatarios: [], hosts: [] };
            this.dialog = true;
        },

        fechar() {
            this.dialog = false;
            if (this.$refs.form) this.$refs.form.resetValidation();
        },

        salvar() {
            if (!this.$refs.form.validate()) return;

            if (!this.form.destinatarios.length) {
                this.notify('Informe ao menos um destinatário', 'error');
                return;
            }

            const regra = { ...this.form };

            if (regra.escopo === 'todos') {
                regra.hosts = [];
            }

            this.$emit('salvar', regra);
            this.dialog = false;
        },
    },
};
</script>
